import { useEffect, useRef, useState } from 'react'
import type { OutputFormat, TranscriptionResult } from '../../types/transcription'
import { useTranscription } from '../../hooks/useTranscription'
import { sha256Hex } from '../../lib/fileHash'
import { afterPaint } from '../../lib/afterPaint'
import { EXPORT_FORMATS } from '../../lib/subtitleConvert'
import { takeIncomingFormat, FORMAT_PARAM } from '../../lib/incomingFormat'
import { findHistoryByHash, updateHistoryViewFormat, type HistoryEntry } from '../../lib/historyStore'
import { trackAudioSent, trackAudioTranscribed, trackLimitExceeded, trackDuplicateUpload, type CreditProps } from '../../analytics/posthog'
import { mediaDurationSeconds } from '../../lib/mediaDuration'
import { useT, useI18n } from '../../i18n/useT'
import { Toast } from '../ui/Toast'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { TranscriptionForm, type PickedFile } from './TranscriptionForm'
import { ResultPanel } from '../results/ResultPanel'

interface TranscriptionCardProps {
  credits: number
  monthlyCredits?: boolean
  hasCredits: boolean
  isSubscribed?: boolean
  planName?: string | null
  onCreditsUpdate: (remaining: number | null) => void
  onLimitReached: () => void
  /** The backend refused the upload with 403 — server-side credits are gone
   * even if the local state disagrees (anonymous per-IP cap) */
  onServerLimit: () => void
}

// Plan allocations mirror PricingSection — analytics-only, approximate for
// grandfathered balances
const PLAN_LIMITS: Record<string, number> = { Basic: 20, Pro: 50, Ultimate: 150 }

interface PendingDuplicate {
  file: File
  language: string
  hash: string | null
  entry: HistoryEntry
}

const FORMAT_STORAGE_KEY = 'stt_format'

/** Format choice survives a page reload — a user who works in SRT shouldn't
 * be reset to Plain Text every visit. */
function savedFormat(): OutputFormat {
  try {
    const saved = localStorage.getItem(FORMAT_STORAGE_KEY)
    if (saved && EXPORT_FORMATS.some((f) => f.format === saved)) return saved as OutputFormat
  } catch {
    // storage unavailable (private mode) — fall through
  }
  return 'text'
}

function rememberFormat(fmt: OutputFormat): void {
  try {
    localStorage.setItem(FORMAT_STORAGE_KEY, fmt)
  } catch {
    // storage unavailable — the choice just won't survive a reload
  }
}

/** A visitor coming from /audio-to-vtt/ wants VTT, not the Plain Text default
 * (nor the SRT they happened to use last month) — the landing page they chose
 * is the freshest signal there is, and it becomes the saved choice. Taken
 * once per arrival: the campaign tag stays in the address for analytics, and
 * reading it again on every load overruled the format the user picked next. */
function initialFormat(): OutputFormat {
  const arrived = takeIncomingFormat()
  if (arrived) rememberFormat(arrived)
  return arrived ?? savedFormat()
}

export function TranscriptionCard({
  credits,
  monthlyCredits,
  hasCredits,
  isSubscribed,
  planName,
  onCreditsUpdate,
  onLimitReached,
  onServerLimit,
}: TranscriptionCardProps) {
  const t = useT()
  const { bcp47 } = useI18n()
  const { isLoading, uploadProgress, progress, result, playbackAudio, error, transcribe, cancel, reset } =
    useTranscription(onServerLimit)
  const [lastLanguage, setLastLanguage] = useState('')
  const [lastFile, setLastFile] = useState<File | null>(null)
  /** The file as the person sees it — the video, when what we upload is its
   * extracted audio track. Both fields or neither: a video's name beside an
   * audio track's size describes nothing that exists. */
  const [lastPicked, setLastPicked] = useState<PickedFile | null>(null)
  const [lastHash, setLastHash] = useState<string | null>(null)
  // Display format — switching it converts the existing result locally,
  // free of charge; only uploading a file costs a credit.
  const [format, setFormat] = useState<OutputFormat>(initialFormat)

  // The arrival signal has been read and saved — take it out of the URL, so a
  // format the user picks afterwards isn't overridden by a reload. The utm_*
  // params stay: analytics reads them once consent is answered.
  useEffect(() => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(FORMAT_PARAM)) return
    url.searchParams.delete(FORMAT_PARAM)
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  }, [])

  function handleFormatChange(fmt: OutputFormat) {
    setFormat(fmt)
    rememberFormat(fmt)
    // The history card reopens on the format the user was actually viewing
    if (result?.job_id) void updateHistoryViewFormat(result.job_id, fmt)
  }
  const [showDupToast, setShowDupToast] = useState(false)
  // Hashing (file.arrayBuffer() on up to 75 MB) happens BEFORE transcribe()
  // flips isLoading — without this the button looks dead while it runs
  const [preparing, setPreparing] = useState(false)
  const [pendingDup, setPendingDup] = useState<PendingDuplicate | null>(null)
  // Content hash + language of the transcription currently on screen
  const chargedRef = useRef<{ hash: string | null; language: string } | null>(null)

  async function handleSubmit(file: File, language: string, picked: PickedFile) {
    if (isLoading || preparing) return
    setPreparing(true)
    // What the person picked, which is the video for a video — the upload is
    // its extracted track and carries an audio extension the server accepts
    setLastPicked(picked)
    try {
      await afterPaint() // let the spinner render before the heavy part
      await submitPrepared(file, language)
    } finally {
      setPreparing(false)
    }
  }

  async function submitPrepared(file: File, language: string) {
    const hash = await sha256Hex(file)

    // Same audio, same language, result already on screen → nothing to pay for
    if (
      result &&
      hash &&
      chargedRef.current &&
      chargedRef.current.hash === hash &&
      chargedRef.current.language === language
    ) {
      setShowDupToast(true)
      return
    }

    // Same audio transcribed earlier (any name — the hash is the identity):
    // offer the free copy from the local history before charging again
    if (hash) {
      const previous = await findHistoryByHash(hash, language)
      if (previous) {
        setPendingDup({ file, language, hash, entry: previous })
        return
      }
    }

    await startTranscription(file, language, hash)
  }

  function creditInfo(): CreditProps {
    return {
      maxLimit: (planName && PLAN_LIMITS[planName]) || 2,
      limitRemaining: credits,
      subscriptionType: isSubscribed && planName ? planName.toLowerCase() : 'free',
    }
  }

  async function startTranscription(file: File, language: string, hash: string | null) {
    setLastLanguage(language)
    setLastFile(file)
    setLastHash(hash)
    if (!hasCredits) {
      trackLimitExceeded(creditInfo())
      onLimitReached()
      return
    }

    // Duration comes from a metadata-only decode — the event fires as soon
    // as it resolves (or times out), never delaying the upload itself
    const credits_ = creditInfo()
    void mediaDurationSeconds(file).then((durationSeconds) => {
      trackAudioSent({
        language,
        format,
        fileExtension: file.name.split('.').pop() || '',
        fileSize: file.size,
        durationSeconds,
        isSubscribed: !!isSubscribed,
        credits: credits_,
      })
    })

    // The charged call uses the chosen format; the backend then fetches the
    // remaining formats in the background — 1 upload = 1 credit = every format.
    const res = await transcribe(file, language, format)

    if (res) {
      chargedRef.current = { hash, language }
      trackAudioTranscribed()
      onCreditsUpdate(res.credits_remaining)
    }
  }

  function handleDupTranscribeAgain() {
    if (!pendingDup) return
    trackDuplicateUpload('transcribe_again')
    const { file, language, hash } = pendingDup
    setPendingDup(null)
    void startTranscription(file, language, hash)
  }

  return (
    // scroll-mt: the header is sticky and 64 px tall, so a jump to
    // #transcription — which is where every static landing's CTA and the
    // sign-up returnTo point — put the card's top edge under it and hid the
    // Language and Output format rows completely. The other three anchor
    // targets carry the same margin; this one was missed because it is the
    // only section with no top padding of its own to absorb the overlap.
    // 20 rather than 16 so the card clears the header's border instead of
    // touching it, matching /history's cards under the same header.
    <section id="transcription" className="pb-16 scroll-mt-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6 sm:p-8">
            <TranscriptionForm
              isLoading={isLoading || preparing}
              credits={credits}
              monthlyCredits={monthlyCredits}
              format={format}
              onFormatChange={handleFormatChange}
              onSubmit={handleSubmit}
            />
          </div>

          {(isLoading || result || error) && (
            <div className="border-t border-border">
              <ResultPanel
                isLoading={isLoading}
                result={result as TranscriptionResult | null}
                error={error}
                onReset={reset}
                language={lastLanguage}
                format={format}
                sourceName={lastPicked?.name ?? lastFile?.name}
                sourceSize={lastPicked?.size ?? lastFile?.size}
                sourceHash={lastHash ?? undefined}
                sourceFile={lastFile}
                playbackAudio={playbackAudio}
                uploadProgress={uploadProgress}
                // A long recording goes up in parts; five minutes of nothing
                // but a spinner is not an acceptable answer
                progress={progress}
                onCancel={isLoading ? cancel : undefined}
              />
            </div>
          )}
        </div>
      </div>

      <Toast
        message={t('dup.toast')}
        isVisible={showDupToast}
        onHide={() => setShowDupToast(false)}
        duration={3000}
      />

      <Modal
        isOpen={pendingDup !== null}
        onClose={() => {
          trackDuplicateUpload('dismissed')
          setPendingDup(null)
        }}
        title={t('dup.title')}
      >
        <p className="text-sm text-text-secondary">
          {pendingDup &&
            t('dup.first', {
              name: pendingDup.entry.filename,
              date: new Date(pendingDup.entry.createdAt).toLocaleDateString(bcp47, {
                day: '2-digit', month: 'short', year: 'numeric',
              }),
            })}{' '}
          {t('dup.second')}
        </p>
        {/* Centred, not right-aligned: two long labels do not fit on one
            line in every language, and `justify-end` stacked the wrapped
            one against the right edge in a ragged staircase. Centring reads
            as one group whether it wraps or not. */}
        <div className="flex justify-center gap-2 mt-5 flex-wrap">
          <Button variant="secondary" size="sm" onClick={handleDupTranscribeAgain}>
            {t('dup.again')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              trackDuplicateUpload('open_history')
              // Deep-link straight to the matched entry — landing on the
              // generic list and hunting for the record defeats the offer
              const id = pendingDup ? `?open=${encodeURIComponent(pendingDup.entry.id)}` : ''
              window.location.href = `/history${id}`
            }}
          >
            {t('dup.open')}
          </Button>
        </div>
      </Modal>
    </section>
  )
}
